/**
 * Feature Team Colors Utility
 * Provides consistent color management for dynamic feature teams
 */

// Professional color palette for feature teams (20 distinct colors)
export const TEAM_COLORS = [
  '#4CAF50', // Green
  '#2196F3', // Blue
  '#FF9800', // Orange
  '#9C27B0', // Purple
  '#F44336', // Red
  '#00BCD4', // Cyan
  '#795548', // Brown
  '#607D8B', // Blue Grey
  '#E91E63', // Pink
  '#3F51B5', // Indigo
  '#CDDC39', // Lime
  '#FF5722', // Deep Orange
  '#009688', // Teal
  '#673AB7', // Deep Purple
  '#FFC107', // Amber
  '#8BC34A', // Light Green
  '#03A9F4', // Light Blue
  '#FF6F00', // Orange Accent
  '#6A1B9A', // Purple Accent
  '#D32F2F'  // Red Accent
];

// Color modes for node coloring
export const COLOR_MODES = {
  TYPE: 'type',
  TEAM: 'team',
  COMBINED: 'combined'
};

/**
 * Simple hash function to ensure consistent color assignment
 * @param {string} str - String to hash
 * @returns {number} - Hash value
 */
export const simpleHash = (str) => {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Get consistent color for a team name
 * @param {string} teamName - Name of the feature team
 * @returns {string} - Hex color code
 */
export const getTeamColor = (teamName) => {
  if (!teamName || teamName === 'No Team') {
    return '#78909C'; // Grey for no team
  }
  const hash = simpleHash(teamName);
  const colorIndex = hash % TEAM_COLORS.length;
  return TEAM_COLORS[colorIndex];
};

/**
 * Get color with alpha transparency
 * @param {string} color - Hex color code
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} - RGBA color string
 */
export const getTeamColorWithAlpha = (color, alpha = 0.7) => {
  // Convert hex to RGB
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
};

/**
 * Generate color map for all teams in nodes
 * @param {Array} nodes - Array of node objects
 * @returns {Object} - Map of team names to colors
 */
export const generateTeamColorMap = (nodes) => {
  const teams = new Set();
  nodes.forEach(node => {
    const team = node.featureTeam || 'No Team';
    teams.add(team);
  });

  const colorMap = {};
  teams.forEach(team => {
    colorMap[team] = getTeamColor(team);
  });

  return colorMap;
};

/**
 * Generate legend data for teams
 * @param {Array} nodes - Array of node objects
 * @returns {Array} - Array of legend items with team, color, and count
 */
export const generateTeamLegendData = (nodes) => {
  const teamCounts = {};
  
  // Count nodes per team
  nodes.forEach(node => {
    const team = node.featureTeam || 'No Team';
    teamCounts[team] = (teamCounts[team] || 0) + 1;
  });

  // Generate legend items
  return Object.entries(teamCounts).map(([team, count]) => ({
    team,
    color: getTeamColor(team),
    count
  })).sort((a, b) => b.count - a.count); // Sort by count descending
};

/**
 * Get node color based on type (legacy system)
 * @param {string} type - Node type
 * @returns {Object} - Color object with background and border
 */
const getNodeColorByType = (type) => {
  switch (type) {
    case "Feature":
      return { background: "#4CAF50", border: "#2E7D32" };
    case "Feature Team":
      return { background: "#f078d6", border: "#9c58db" };
    case "Component":
      return { background: "#2196F3", border: "#1565C0" };
    case "API":
      return { background: "#FFC107", border: "#FFA000" };
    case "Service":
      return { background: "#9C27B0", border: "#7B1FA2" };
    case "User":
      return { background: "#F44336", border: "#C62828" };
    case "Module":
      return { background: "#3F51B5", border: "#303F9F" };
    default:
      return { background: "#78909C", border: "#455A64" };
  }
};

/**
 * Get node color based on the selected color mode
 * @param {Object} node - Node object with type and featureTeam
 * @param {string} colorMode - Color mode ('type', 'team', 'combined')
 * @returns {Object} - Color object with background and border
 */
export const getNodeColor = (node, colorMode = COLOR_MODES.TYPE) => {
  if (!node) return { background: "#78909C", border: "#455A64" };

  switch (colorMode) {
    case COLOR_MODES.TYPE:
      return getNodeColorByType(node.type);
      
    case COLOR_MODES.TEAM: {
      const teamColor = getTeamColor(node.featureTeam);
      // Generate darker border color
      const borderColor = getTeamColorWithAlpha(teamColor, 0.8);
      return { 
        background: teamColor, 
        border: borderColor.replace('rgba', 'rgb').replace(', 0.8)', ')') 
      };
    }
    
    case COLOR_MODES.COMBINED: {
      // Use team color as base, but vary brightness based on type
      const teamColor = getTeamColor(node.featureTeam);
      const typeColors = getNodeColorByType(node.type);
      
      // Blend team and type colors
      return {
        background: teamColor,
        border: typeColors.border
      };
    }
    
    default:
      return getNodeColorByType(node.type);
  }
};

/**
 * Get statistics for feature teams
 * @param {Array} nodes - Array of node objects
 * @returns {Object} - Statistics object with team counts and totals
 */
export const getTeamStatistics = (nodes) => {
  const stats = {
    totalNodes: nodes.length,
    totalTeams: 0,
    teams: {},
    noTeamCount: 0
  };

  nodes.forEach(node => {
    const team = node.featureTeam || 'No Team';
    if (team === 'No Team') {
      stats.noTeamCount++;
    } else {
      stats.teams[team] = (stats.teams[team] || 0) + 1;
    }
  });

  stats.totalTeams = Object.keys(stats.teams).length;
  
  return stats;
};